import { memberAccessOf, type MemberAccess, type NetworkMemberAccess } from './memberAccess';
import type { NamedGroup } from './accessSentence';
import type { Grant } from './grant';
import type { PeopleMemberRow, PersonGroup } from './studyPeople';

const namedGroupOf = ({ id, name }: PersonGroup): NamedGroup => ({ identifier: id, name });

export interface UserCacheModel {
    identifier: string;
    email: string;
    uri: string;
    user: string;
    data: string;
    avatar?: string;
    name?: string;
    b64Identifier?: string;
}

/**
 * A deactivated member's pause (users decisions 140 and 141): when, by whom, and the own grant and
 * groups Activate puts back in effect.
 */
export interface MemberDeactivation {
  at: string;
  /** Null when the server can't name who deactivated them. */
  byName: string | null;
  kept: { own: Grant; groups: NamedGroup[] };
}

export interface UserUpdatePayloadModel {
  name?: string;
  avatar?: string;
}

export interface NetworkUser extends NetworkMemberAccess {
    identifier?: string;
    email: string;
    name: string;
    avatar: string;
    organizationId: string;
    creation: string;
    status?: string;
    inviteIdentifier?: string;
    userId?: string;
    notificationProfile?: string;
    groups?: NamedGroup[];
    expiresAt?: string;
    createdAt?: string;
}

interface IUser {
    identifier: string;
    email: string;
    name: string;
    avatar: string;
    organizationId: string;
    status?: string;
    b64Identifier?: string;
    notificationProfile?: string;
    groups?: NamedGroup[];
    expiresAt?: string;
    createdAt?: string;
    access?: MemberAccess;
    deactivation?: MemberDeactivation;
}

export class UserModel {
  identifier: string;
  email: string;
  name: string;
  avatar: string;
  organizationIdentifier: string;
  status?: string;
  b64Identifier?: string;

  notificationProfile?: string;
  groups?: NamedGroup[];
  expiresAt?: string;
  createdAt?: string;
  /** A member's access as the People list works it out: `access.own` is their grant. */
  access?: MemberAccess;
  /** Set while the member is deactivated, and only from the People list. */
  deactivation?: MemberDeactivation;

  constructor({
    identifier,
    email,
    name,
    avatar,
    organizationId,
    status,
    b64Identifier,
    notificationProfile,
    groups,
    expiresAt,
    createdAt,
    access,
    deactivation
  } : IUser) {
    this.identifier = identifier;
    this.email = email;
    this.name = name;
    this.avatar = avatar;
    this.organizationIdentifier = organizationId;
    this.status = status;
    this.b64Identifier = b64Identifier;
    this.notificationProfile = notificationProfile;
    this.groups = groups;
    this.expiresAt = expiresAt;
    this.createdAt = createdAt;
    this.access = access;
    this.deactivation = deactivation;
  }

  static mapFromBackend(networkUser: NetworkUser, identifier: string = '') {
    return new UserModel({
      identifier: networkUser.identifier ? networkUser.identifier : identifier,
      email: networkUser.email,
      name: networkUser.name,
      avatar: networkUser.avatar,
      organizationId: networkUser.organizationId,
      status: networkUser.status,
      b64Identifier: networkUser.userId,
      notificationProfile: networkUser.notificationProfile,
      groups: networkUser.groups?.map(group => ({ identifier: group.identifier, name: group.name })),
      expiresAt: networkUser.expiresAt,
      createdAt: networkUser.createdAt,
      access: memberAccessOf(networkUser)
    });
  }

  /** A People list member in the shape the member panel draws. */
  static fromMemberRow(row: PeopleMemberRow) {
    return new UserModel({
      identifier: row.id,
      email: row.email,
      name: row.name ?? '',
      avatar: row.avatar ?? '',
      organizationId: '',
      status: 'Activated',
      notificationProfile: row.notificationProfile ?? undefined,
      groups: row.groups.map(namedGroupOf),
      access: { own: row.own, effective: row.effective, source: row.source, inGroup: row.inGroup },
      deactivation: row.deactivatedAt ?
        {
          at: row.deactivatedAt,
          byName: row.deactivatedBy?.name ?? null,
          // `kept` is the row's own grant and groups again, under the name the panel reads, so a row
          // without it still opens as deactivated rather than as an active member's.
          kept: { own: (row.kept ?? row).own, groups: (row.kept ?? row).groups.map(namedGroupOf) }
        } :
        undefined
    });
  }
}

/**
 * Whether `identifier` names the signed-in caller, whose own row the People page draws read-only
 * (users decisions 6 and 118). The two ids can arrive in different cases, so the compare ignores case.
 */
export const isCaller = (callerIdentifier: string | undefined, identifier: string): boolean =>
  callerIdentifier?.toLowerCase() === identifier.toLowerCase();

/** Whether two hex identifiers name the same thing; the server and the URL disagree on case. */
export const sameIdentifier = (a: string, b: string): boolean => a.toLowerCase() === b.toLowerCase();
